import { supabase } from '@/lib/supabase';
import type { Version, CreateVersionInput } from '@/features/versions/types';
import { BusinessError } from '@/features/channels/utils/errors';

// Add proper error handling
const handleVersionError = (error: any) => {
  console.error('Version error:', error);
  
  if (error?.code === '23505') {
    if (error.message.includes('unique_provider_draft')) {
      throw new BusinessError('A draft version already exists for this provider');
    }
    if (error.message.includes('unique_provider_region_draft')) {
      throw new BusinessError('A draft version already exists for this region');
    }
    throw new BusinessError('Duplicate entry detected');
  }

  if (error?.code === '23503') {
    throw new BusinessError('Provider not found');
  }

  if (error?.code === 'P0001') {
    if (error.message.includes('Sequential provider can only have one sequence type')) {
      throw new BusinessError('Sequential provider can only have one sequence type');
    }
    if (error.message.includes('invalid input syntax for type integer')) {
      throw new BusinessError('Invalid version number format');
    }
    if (error.message.includes('Invalid version number format')) {
      throw new BusinessError('Invalid version number format detected');
    }
    if (error.message.includes('Provider not found')) {
      throw new BusinessError('Provider not found');
    }
    if (error.message.includes('A draft version already exists for this region')) {
      throw new BusinessError('A draft version already exists for this region');
    }
    if (error.message.includes('A draft version already exists')) {
      throw new BusinessError('A draft version already exists for this provider');
    }
    // CRITICAL: Pass through duplicate channel errors as-is for modal handling
    if (error.message.includes('is already in use by another non-local, non-duplicate channel')) {
      // Re-throw the original error with its code intact for proper handling
      throw error;
    }
    // Generic P0001 error
    throw new BusinessError(error.message || 'Database operation failed');
  }

  if (error?.code === '0A000') {
    throw new BusinessError('Database operation failed. Please try again.');
  }

  if (error instanceof BusinessError) {
    throw error;
  }

  // Default error handler
  throw new BusinessError(error?.message || 'An unexpected error occurred');
};

export const versionService = {
  checkVersionExists: async (versionId: number) => {
    try {
      const { data, error } = await supabase
        .from('provider_versions')
        .select('id')
        .eq('id', versionId)
        .single();

      if (error) throw error;
      if (!data) {
        throw new BusinessError('Version not found');
      }
    } catch (error) {
      handleVersionError(error);
    }
  },

  checkVersionStatus: async (versionId: number) => {
    try {
      const { data, error } = await supabase
        .from('provider_versions')
        .select('status')
        .eq('id', versionId)
        .single();

      if (error) throw error;
      if (data.status === 'published') {
        throw new BusinessError('Cannot modify published version');
      }
    } catch (error) {
      handleVersionError(error);
    }
  },

  createVersion: async (providerId: number, data: CreateVersionInput) => {
    try {
      // Validate input parameters
      console.log('Input validation:', {
        providerId,
        description: data.description,
        hasProviderId: providerId !== undefined && providerId !== null,
        isProviderIdNumber: typeof providerId === 'number',
        descriptionType: typeof data.description
      });

      // Check if this is a regional version
      const isRegionalVersion = 'region_id' in data && data.region_id !== undefined && data.region_id !== null;
      
      // Get provider details to check sequence status
      const { data: provider, error: providerError } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', providerId)
        .single();

      if (providerError) throw providerError;

      console.log('Provider check:', {
        provider,
        hasSequenceNumbers: provider?.has_sequence_numbers,
      });

      if (isRegionalVersion) {
        // For regional versions, check that specific region for existing drafts
        const regionId = (data as any).region_id;
        
        const { data: existingDrafts, error: draftError } = await supabase
          .from('provider_versions')
          .select('id, version_number, region_id')
          .eq('provider_id', providerId)
          .eq('region_id', regionId)
          .eq('status', 'draft');
          
        if (draftError) throw draftError;
        
        if (existingDrafts && existingDrafts.length > 0) {
          // Draft exists for this region
          throw new BusinessError(`A draft version (${existingDrafts[0].version_number}) already exists for this region. Please publish or delete the existing draft before creating a new one.`);
        }
        
        // Verify the region exists and belongs to this provider
        const { data: regionData, error: regionCheckError } = await supabase
          .from('regions')
          .select('id, name')
          .eq('id', regionId)
          .eq('provider_id', providerId)
          .single();
          
        if (regionCheckError || !regionData) {
          throw new BusinessError(`The selected region does not exist or does not belong to this provider.`);
        }
        
        console.log(`Creating version for region: ${regionData.name} (${regionId})`);
        
        // Regional version - use our unified function
        const { data: versionData, error } = await supabase
          .rpc('create_regional_version', {
            p_provider_id: providerId,
            p_region_id: regionId,
            p_description: data.description || '',
            p_is_sequential: provider.has_sequence_numbers,
            p_copy_from_published: true
          });

        if (error) throw error;
        return versionData;
      } else {
        // For non-regional versions (when user selects "No region (global)"), 
        // we need to create a default region or use a special indicator
        
        // Get all regions for this provider
        const { data: regions, error: regionsError } = await supabase
          .from('regions')
          .select('id, name')
          .eq('provider_id', providerId);
            
        if (regionsError) throw regionsError;
        
        // If provider has no regions, create a default region
        if (!regions || regions.length === 0) {
          try {
            // Create a default region first
            const { data: newRegion, error: regionError } = await supabase
              .from('regions')
              .insert({
                provider_id: providerId,
                name: 'Default Region',
                zip_code: '00000'
              })
              .select()
              .single();
                  
            if (regionError) throw regionError;
            
            console.log(`Created default region: ${newRegion.name} (${newRegion.id})`);
              
            // Now create the version with the new region
            const { data: versionData, error } = await supabase
              .rpc('create_regional_version', {
                p_provider_id: providerId,
                p_region_id: newRegion.id,
                p_description: data.description || '',
                p_is_sequential: provider.has_sequence_numbers,
                p_copy_from_published: true
              });

            if (error) throw error;
            return versionData;
          } catch (err) {
            console.error('Error creating default region:', err);
            throw new BusinessError('Failed to create default region for this provider. Please try again.');
          }
        } else {
          // If provider already has regions, ask the user to select a specific region
          throw new BusinessError('Please select a specific region for the new version. This provider already has regions configured.');
        }
      }
    } catch (error) {
      console.error('Create version error:', error);
      handleVersionError(error);
    }
  },

  updateVersion: async (versionId: number, updates: Partial<Version>) => {
    try {
      await versionService.checkVersionStatus(versionId);
      
      const { data, error } = await supabase
        .from('provider_versions')
        .update(updates)
        .eq('id', versionId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      handleVersionError(error);
    }
  },

  deleteVersion: async (versionId: number, force: boolean = false) => {
    const { error } = await supabase
      .rpc('delete_version', {
        p_version_id: versionId,
        p_force: force
      });

    if (error) throw error;
  },

  copyVersionData: async (sourceId: number, targetId: number) => {
    try {
      const { error } = await supabase
        .rpc('copy_version_data', {
          source_version_id: sourceId,
          target_version_id: targetId
        });

      if (error) throw error;
    } catch (error) {
      handleVersionError(error);
    }
  },

  getVersionWithDetails: async (id: number) => {
    try {
      const { data, error } = await supabase
        .from('provider_versions')
        .select(`
          *,
          channels:channels(count),
          categories:categories(count),
          content_groups:content_groups(count)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as Version & {
        channels: { count: number }[];
        categories: { count: number }[];
        content_groups: { count: number }[];
      };
    } catch (error) {
      handleVersionError(error);
    }
  },

  updateVersionStatus: async (id: number, status: Version['status']) => {
    try {
      const { data, error } = await supabase
        .from('provider_versions')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Version;
    } catch (error) {
      handleVersionError(error);
    }
  },

  createVersionWithResolutions: async (providerId: number, data: any) => {
    try {
      // Get provider details to check sequence status
      const { data: provider, error: providerError } = await supabase
        .from('providers')
        .select('has_sequence_numbers')
        .eq('id', providerId)
        .single();

      if (providerError) throw providerError;
      
      // Use the enhanced RPC function that accepts duplicate channel resolutions
      // @ts-ignore - TypeScript types not updated yet for new parameter
      const { data: versionData, error } = await supabase
        .rpc('create_regional_version', {
          p_provider_id: providerId,
          p_region_id: data.region_id || null,
          p_description: data.description || '',
          p_is_sequential: provider.has_sequence_numbers,
          p_copy_from_published: true,
          p_duplicate_channel_resolutions: data.duplicate_channel_resolutions || null
        } as any);

      if (error) throw error;
      return versionData as Version;
    } catch (error) {
      handleVersionError(error);
    }
  }
}; 